import React, {useEffect, useMemo, useState} from 'react';
import {useAuth} from '../../../helpers/AuthContext';
import {createSellerMediaApi} from '../Media/mediaApi';
import {isBrowserPreviewableImage, mediaKind} from '../Media/MediaCard';

function normalizeMedia(media) {
    return Array.isArray(media) ? media : Object.values(media || {});
}

async function readJson(response, fallbackMessage) {
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(payload.error || payload.message || fallbackMessage);
    }
    return payload;
}

export default function ProductGalleryEditor({productId, media, onChange, onError}) {
    const {fetch, API_URL, DASHBOARD_URL} = useAuth();
    const mediaApi = useMemo(
        () => createSellerMediaApi({fetch, apiUrl: API_URL}),
        [API_URL, fetch],
    );
    const [items, setItems] = useState(() => normalizeMedia(media));
    const [busyAction, setBusyAction] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        setItems(normalizeMedia(media));
    }, [media]);

    const applyItems = nextItems => {
        const normalized = normalizeMedia(nextItems);
        setItems(normalized);
        onChange?.(normalized);
    };

    // Mutations address an entry by its stable uuid. The path index is only a
    // fallback for entries stored before uuids existed, because it goes stale as
    // soon as the gallery shifts under another write.
    const mutateGallery = async (index, action, body, fallbackMessage) => {
        setBusyAction(`${action}-${index}`);
        setError('');
        const entryUuid = items[index]?.uuid;
        const legacyPath = `/media/${index}${action === 'caption' ? '' : `/${action}`}`;
        const path = entryUuid
            ? `/media/${action === 'caption' ? 'update' : action}`
            : legacyPath;
        const requestBody = entryUuid ? {uuid: entryUuid, ...body} : body;
        try {
            const response = await fetch(
                `${API_URL}/v1/seller/products/${productId}${path}`,
                {
                    method: 'POST',
                    ...(requestBody ? {
                        headers: {'Content-Type': 'application/json'},
                        body: JSON.stringify(requestBody),
                    } : {}),
                },
            );
            const payload = await readJson(response, fallbackMessage);
            applyItems(payload.data || []);
        } catch (mutationError) {
            setError(mutationError.message);
            onError?.(mutationError.message);
        } finally {
            setBusyAction('');
        }
    };

    const updateCaption = (index, description) => {
        setItems(current => current.map((item, itemIndex) => (
            itemIndex === index ? {...item, description} : item
        )));
    };

    const saveCaption = index => mutateGallery(
        index,
        'caption',
        {description: items[index]?.description || ''},
        'Could not save the caption.',
    );

    const moveItem = (index, direction) => mutateGallery(
        index,
        'move',
        {direction},
        'Could not move the gallery item.',
    );

    const deleteItem = index => {
        if (!window.confirm('Remove this item from the product gallery?')) return;
        mutateGallery(
            index,
            'delete',
            null,
            'Could not remove the gallery item.',
        );
    };

    const rotateItem = async (index, file) => {
        setBusyAction(`rotate-${index}`);
        setError('');
        try {
            const nextFile = await mediaApi.rotate(file.uuid);
            applyItems(items.map((item, itemIndex) => (
                itemIndex === index ? {...item, file: nextFile} : item
            )));
        } catch (rotateError) {
            setError(rotateError.message);
            onError?.(rotateError.message);
        } finally {
            setBusyAction('');
        }
    };

    if (!items.length) {
        return <p className="product-gallery-editor-empty">No gallery media added yet.</p>;
    }

    return (
        <section className="product-gallery-editor" aria-labelledby="product-gallery-heading">
            <h3 id="product-gallery-heading">Media Gallery</h3>
            <p className="help">Edit captions, rotate images, reorder, or remove gallery media.</p>

            {error && <div className="flash error" role="alert"><p>{error}</p></div>}

            <div className="product-gallery-editor-grid">
                {items.map((item, index) => {
                    const file = item.file || {};
                    const kind = mediaKind(file);
                    const label = file.title || file.filename || `Gallery item ${index + 1}`;
                    const previewUrl = file.cdnUrl || file.cdnPath || '';
                    const imageUrl = previewUrl
                        ? `${previewUrl}?w=640&h=480&etag=${file.etag || file.uuid || ''}`
                        : '';
                    const itemBusy = Boolean(busyAction);

                    return (
                        <figure
                            className="product-gallery-editor-item"
                            key={item.uuid || file.uuid || `${item.fileId}-${index}`}
                        >
                            <div className="product-gallery-editor-preview">
                                {kind === 'image' && isBrowserPreviewableImage(file) && imageUrl ? (
                                    <img
                                        src={imageUrl}
                                        alt={item.description || file.metadata?.altText || label}
                                    />
                                ) : kind === 'video' && file.uuid ? (
                                    <video controls preload="metadata">
                                        <source
                                            src={`${DASHBOARD_URL || ''}/account/vcdn/${file.uuid}`}
                                            type={file.mimetype}
                                        />
                                    </video>
                                ) : (
                                    <span className="product-gallery-editor-file">
                                        <i className="far fa-file-image" aria-hidden="true"></i>
                                        <span>{label}</span>
                                    </span>
                                )}
                            </div>

                            <figcaption>
                                <label htmlFor={`gallery-caption-${index}`}>Caption</label>
                                <input
                                    id={`gallery-caption-${index}`}
                                    type="text"
                                    value={item.description || ''}
                                    placeholder="Optional image or video caption"
                                    onChange={event => updateCaption(index, event.target.value)}
                                    disabled={itemBusy}
                                />
                                <button
                                    type="button"
                                    className="button small"
                                    onClick={() => saveCaption(index)}
                                    disabled={itemBusy}
                                >
                                    Save Caption
                                </button>
                            </figcaption>

                            <div className="product-gallery-editor-actions">
                                <button
                                    type="button"
                                    className="button small secondary"
                                    aria-label={`Move ${label} left`}
                                    onClick={() => moveItem(index, 'up')}
                                    disabled={itemBusy || index === 0}
                                >
                                    <i className="fas fa-arrow-left" aria-hidden="true"></i> Move
                                </button>
                                <button
                                    type="button"
                                    className="button small secondary"
                                    aria-label={`Move ${label} right`}
                                    onClick={() => moveItem(index, 'down')}
                                    disabled={itemBusy || index === items.length - 1}
                                >
                                    Move <i className="fas fa-arrow-right" aria-hidden="true"></i>
                                </button>
                                {kind === 'image' && file.uuid && (
                                    <button
                                        type="button"
                                        className="button small secondary"
                                        onClick={() => rotateItem(index, file)}
                                        disabled={itemBusy}
                                    >
                                        <i className="fas fa-rotate-right" aria-hidden="true"></i>
                                        {' '}Rotate 90°
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="button small danger"
                                    onClick={() => deleteItem(index)}
                                    disabled={itemBusy}
                                >
                                    Remove
                                </button>
                            </div>
                        </figure>
                    );
                })}
            </div>
        </section>
    );
}
